import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

interface UseSimpleTextPasteProps {
  onPaste?: (text: string, targetField?: string) => void;
}

export const useSimpleTextPaste = ({ onPaste }: UseSimpleTextPasteProps = {}) => {
  const [selectedText, setSelectedText] = useState<string>('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const { toast } = useToast();

  // Handle text selection from PDF
  const handleTextSelect = useCallback((text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText) return;

    setSelectedText(trimmedText);
    
    // If a field is focused, paste immediately
    if (focusedField && trimmedText) {
      onPaste?.(trimmedText, focusedField);
      
      toast({
        title: "Text Pasted",
        description: `Pasted to ${focusedField}`,
        duration: 2000
      });
    }
  }, [focusedField, onPaste, toast]);

  // Handle field focus
  const handleFieldFocus = useCallback((fieldName: string) => {
    setFocusedField(fieldName);
  }, []);

  // Handle field blur
  const handleFieldBlur = useCallback(() => {
    // Keep field focused for a short time to allow PDF interaction
    setTimeout(() => setFocusedField(null), 200);
  }, []);

  return {
    selectedText,
    focusedField,
    handleTextSelect,
    handleFieldFocus,
    handleFieldBlur
  };
};
